import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import { Helmet } from "react-helmet-async";
import "../../css/Administrator/AdministratorAccessPage.css";
import { loadStaffDepartmentAccess } from "./utils/getStaffDepartmentAccess";
import { getVisibleAdviserTabs } from "../Adviser/Utils/adviserTabAccess";
import OptionList from "../DesignSystem/SelectionControls/OptionList";

const matchesQuery = (staff, q) => {
  if (!q) return true;

  const hay = [
    staff.email,
    ...(staff.permissions || []),
    ...(staff.access || []).flatMap((item) => [
      item.department,
      ...(item.subDepartments || []),
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return hay.includes(q.toLowerCase());
};

const initialsFromEmail = (email = "") => {
  const clean = String(email).split("@")[0] || "";
  const parts = clean.split(/[._-]+/).filter(Boolean);

  if (parts.length === 0) return "ST";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const isEndedWithoutEmail = (staff) =>
  Boolean(staff?.employmentEndDate) && staff?.hasEmail === false;

export default function AdministratorAccessPage() {
  const [staffAccess, setStaffAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let alive = true;

    const fetchStaffAccess = async () => {
      setLoading(true);

      const result = await loadStaffDepartmentAccess();

      if (!alive) return;

      if (result.success) {
        const data = result.data || [];
        setStaffAccess(data);
        setSelectedId((prev) => prev || data?.[0]?.id || null);
      }

      setLoading(false);
    };

    fetchStaffAccess();

    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return (staffAccess || []).filter((staff) => matchesQuery(staff, q));
  }, [staffAccess, q]);

  const selected = useMemo(() => {
    return (
      filtered.find((staff) => staff.id === selectedId) ||
      staffAccess.find((staff) => staff.id === selectedId) ||
      null
    );
  }, [filtered, staffAccess, selectedId]);

  useEffect(() => {
    if (!filtered.length) {
      setSelectedId(null);
      return;
    }

    const stillExists = filtered.some((staff) => staff.id === selectedId);
    if (!stillExists) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const contractOptions = [
    { value: "ltd", label: "Ltd Company" },
    { value: "partnership", label: "Partnership" },
    { value: "sole-trader", label: "Sole Trader" },
    { value: "trading-style", label: "Trading Style" },
    { value: "ri-contract", label: "RI Contract" },
  ];

  const [contractType, setContractType] = useState("ltd");
  const [contractTypes, setContractTypes] = useState(["ltd"]);

  return (
    <div className="main-content">
      <Helmet>
        <title>Administrator Access Overview</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <div className="adminAccessPage gap-m column">
          <Breadcrumb
            items={[
              { label: "Administrator", to: "/Department/Administrator" },
              { label: "Staff Access Overview" },
            ]}
          />

          <div className="row space-between">
            <div className="FP_H3 text-primary-1">
              Staff Members:
              {loading ? "Loading…" : `${filtered.length}`}
            </div>
            <input
              className="adminAccessSearch Content-Small"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email, permission, department, or feature…"
            />
          </div>

          {/* <header className="adminAccessTop">
          </header> */}
          {/* <OptionList
            label="Contract Type"
            options={contractOptions}
            value={contractType}
            onChange={setContractType}
          />

          <OptionList
            label="Contract Types - multiple"
            multiple
            options={contractOptions}
            value={contractTypes}
            onChange={setContractTypes}
          /> */}

          <main className="adminAccessGrid">
            <section className="adminAccessPanel adminAccessListPanel">
              <div className="adminAccessPanelBody">
                {loading ? (
                  <div className="adminAccessEmpty Content-Small">
                    Loading staff access…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="adminAccessEmpty Content-Small">
                    No staff members matched your search.
                  </div>
                ) : (
                  <div className="adminAccessScroll">
                    {filtered.map((staff) => {
                      const isInactive = isEndedWithoutEmail(staff);

                      return (
                        <button
                          key={staff.id}
                          type="button"
                          className={`adminAccessRow ${
                            staff.id === selectedId ? "isActive" : ""
                          } ${isInactive ? "isEmploymentEnded" : ""}`}
                          onClick={() => setSelectedId(staff.id)}
                        >
                          <div className="adminAccessAvatar FP_Content_Semibold text-white">
                            {initialsFromEmail(staff.email)}
                          </div>

                          <div className="adminAccessRowMain">
                            <div className="adminAccessRowTop">
                              <span className="adminAccessName Content-SemiBold">
                                {staff.email}
                              </span>
                            </div>

                            <div className="adminAccessSub Content-Small">
                              {(staff.permissions || [])
                                .slice(0, 3)
                                .join(", ") || "No permissions"}
                            </div>

                            {!!staff.access?.length && (
                              <div className="adminAccessTags">
                                {staff.access.map((item) => (
                                  <span className="tag" key={item.department}>
                                    {item.department}
                                  </span>
                                ))}
                                {/* {staff.access.length > 2 ? (
                                <span className="tag muted Content-Small">
                                  +{staff.access.length - 2}
                                </span>
                              ) : null} */}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <section className="adminAccessPanel adminAccessProfilePanel">
              <div className="adminAccessPanelBody">
                {!selected ? (
                  <div className="adminAccessEmpty">
                    Select a staff member to view access details.
                  </div>
                ) : (
                  <StaffAccessProfile staff={selected} />
                )}
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}

function StaffAccessProfile({ staff }) {
  const adviserTabs = getVisibleAdviserTabs(staff.permissions, {
    userEmail: staff.email,
  });

  return (
    <div className="adminProfileWrap">
      <div className="adminProfileHeader">
        <div className="adminProfileHeaderLeft">
          <h2 className="adminProfileName Content-SemiBold">{staff.email}</h2>

          <div className="adminProfileMeta Content-Small">
            <span className="muted">
              {(staff.permissions || []).length} permission
              {(staff.permissions || []).length === 1 ? "" : "s"}
            </span>
            <span className="dot">•</span>
            <span className="muted">
              {(staff.access || []).length} department
              {(staff.access || []).length === 1 ? "" : "s"}
            </span>
                  </div>
                  
        </div>
      </div>

        <div className="card">
          <div className="kv">
            {/* <span className="Content-Small">Raw Permissions</span> */}
            <div className="administrator-chip-row">
              {staff.permissions?.length ? (
                staff.permissions.map((permission) => (
                  <span
                    key={`${staff.id}-${permission}`}
                    className="administrator-chip"
                  >
                    {permission}
                  </span>
                ))
              ) : (
                <b className="Content" style={{color:"black"}} >No permissions</b>
              )}
            </div>
          </div>
        </div>
      <div className="adminProfileCards">

        <div className="card adminDepartmentCard">
          <div className="kv">
            <span className="Content-Small">Adviser tabs</span>
          </div>
          <div className="administrator-chip-row">
            {adviserTabs.map((tab) => (
              <span
                key={`${staff.id}-adviser-tab-${tab.id}`}
                className="administrator-feature-chip"
              >
                {tab.label}
              </span>
            ))}
          </div>
        </div>

        <div className="card adminDepartmentCard">
          <div className="kv">
            <span className="Content-Small">Department Access</span>
          </div>

          <div className="administrator-department-list">
            {staff.access?.length ? (
              staff.access.map((accessItem) => (
                <div
                  key={`${staff.id}-${accessItem.department}`}
                  className="administrator-department-card"
                >
                  <div className="Content-SemiBold">
                    {accessItem.department}
                  </div>

                  <div className="administrator-chip-row">
                    {(accessItem.subDepartments || []).map((feature) => (
                      <span
                        key={`${staff.id}-${accessItem.department}-${feature}`}
                        className="administrator-feature-chip"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <b className="wrap Content">No department access found</b>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
